import {throwError as observableThrowError,  Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient, HttpResponse } from '@angular/common/http';
import { AuthService } from './auth.service';
import { CookieService } from 'ngx-cookie';
import { AppConfigService } from './app-config.service';

@Injectable()
export class UsersService {
	users: any[] = [];
	private http: HttpClient;
	private authService: AuthService;
	usersURL: string;
	userWebServiceCookie: any;
	loginToken = '';
	applicationName = "Operator";

	constructor ( 
		http: HttpClient, 
		private appConfigService: AppConfigService,
		authService: AuthService, 
		private _cookieService: CookieService ) 
	{
		this.http = http;
		this.authService = authService;
		this.usersURL = this.appConfigService.getUsersURL();
	}

	private handleError (error: any): Observable<string> {
		console.log(error);
		const errMsg = (error.message) ? error.message :
			error.status ? `${error.status} - ${error.statusText}` : 'Server error';
		console.error(errMsg); // log to console instead
		return observableThrowError(errMsg);
	}

	//
	// add a user - sets the userName, name, and e-mail
	//
	addUser (newUser: any): Observable<any> {
		return this.createUser(newUser);
	}

	//
	// createUser - helper function to addUser
	// call web services to create a new user - it only creates a new user, setting just the userName
	//
	createUser(newUser: any): Observable<any> {
		console.log(newUser);
		const createUsersURL = this.usersURL + 'users/create';
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };

		// Create the primary user information.
		const formData = { 'username': newUser.username,
							'passwd': newUser.password,
							'name': newUser.name,
							'email': newUser.email,
							'address': newUser.address,
							'mobile': newUser.mobile,
							'organization': newUser.organization,
							'phone': newUser.phone,
							'title': newUser.title };

		return this.http.post(createUsersURL, formData, httpOptions );
	}

	addUserRoles(user: any): Observable<any> {
		//
		// Create a formData object to add the roles for the user.
		//
		const roles = [];
		user.roles.forEach(function(item) {
			// console.log(item);
			roles.push({ 'name': item.name });
		});
		const rolesFormData = { 'username': user.username, 'roles': roles };

		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };

		return this.http.post(this.usersURL + 'users/assignRoles', rolesFormData, httpOptions );
	}

	//
	// call web services to get a user using the userName only
	//
	getUser(username: any): Observable<any> {
		const getUserURL = this.usersURL + 'users/' + username;
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };
		return this.http.get( getUserURL, httpOptions );
	}

	// get roles for an individual user
	getUserRoles(username: any): Observable<any> {
		const getUserRolesURL = this.usersURL + 'users/getRoles?username=' + username;
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };
		return this.http.get( getUserRolesURL, httpOptions );
	}

	// get preferences for an individual user
	getUserPreferences(username: any): Observable<any> {
		const getUserPreferencesURL = this.usersURL + 'users/getUserPreferences?username=' + username + '&application=' + this.applicationName;
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		const httpOptions = { headers: httpHeaders };
		return this.http.get( getUserPreferencesURL, httpOptions );
	}

	// Adds or updates dashboard template based on whether the id has been used before.
	addDashboardTemplate(username, template) {
		this.getUserPreferences(username).subscribe( userProfile => {
			let preferences = userProfile.preferences.preferences;
			console.log(preferences);
			if ( preferences == null ) {
				preferences = {};
			}
			if ( !preferences.hasOwnProperty('dashboardTemplates')) {
				preferences['dashboardTemplates'] = [];
			}

			const templates = preferences.dashboardTemplates;
			// console.log(templates);
			// Search for template in existing templates.
			// If found, replace the existing template.
			// If not found, add to templates.
			let idIndex = -1;
			templates.forEach(function(item, index) {
				if ( item.id === template.id ) {
					// Save index to replace.
					idIndex = index;
				}
			});

			// Template not yet defined, add it.
			if (idIndex === -1 ) {
				templates.push (template);
			} else {
				templates[idIndex] = template;
			}
			console.log(preferences);

			// update in user web services
			this.setUserPreferences (
				{ 	'username': username,
					'preferences': preferences
				}
			).subscribe();
		});
	}

	// Deletes dashboard template based on whether the id has been used before.
	deleteDashboardTemplate(username, template) {
		this.getUserPreferences(username).subscribe( userProfile => {
			let preferences = userProfile.preferences.preferences;
			console.log(preferences);
			if ( preferences == null ) {
				preferences = {};
			}
			if ( !preferences.hasOwnProperty('dashboardTemplates')) {
				return;
			}

			const templates = preferences.dashboardTemplates;
			// console.log(templates);
			// Search for template in existing templates.
			let idIndex = -1;
			templates.forEach(function(item, index) {
				if ( item.id === template.id ) {
					// Save index to delete.
					idIndex = index;
				}
			});

			// Template found - delete it
			if (idIndex != -1 ) {
				console.log('Deleting user dashboard index ', idIndex );
				templates.splice (idIndex, 1);
			}
			console.log(preferences);

			// update in user web services
			this.setUserPreferences (
				{ 	'username': username,
					'preferences': preferences
				}
			).subscribe();
		});
	}

	getAllAvailableUserRoles(): Observable<any> {
		const rolesURL = this.usersURL + 'roles/';
		// console.log(rolesURL);
		return this.http.get(rolesURL);
	}

	// Get permissions for an individual role.
	getRolePermissions(role: any): Observable<any> {
		const getPermissionURL = this.usersURL + 'roles/getPermissions?rolename=' + role;
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };
		return this.http.get( getPermissionURL, httpOptions );
	}

	// Return true if specified user has given permission, false otherwise.
	// If permission is undefined, return true.
	hasPermission(username, permission): Observable<any> {
		console.log(username, permission);
		if ( permission === undefined ) {
			return of(true);
		}

		const permissionURL = this.usersURL + 'users/hasPermission?username=' + username + '&permission=' + permission;
		return this.http.get(permissionURL);
	}

	updateUser(user: any): Observable<any> {
		const editUsersURL = this.usersURL + 'users/update';
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };

		// Create the primary user information.
		const formData = { 'username': user.username,
							'passwd': user.password,
							'name': user.name,
							'email': user.email,
							'address': user.address,
							'mobile': user.mobile,
							'organization': user.organization,
							'phone': user.phone,
							'title': user.title };

		return this.http.post(editUsersURL, formData, httpOptions );
	}

	setUserPreferences(user: any): Observable<any> {
		const setUserPreferencesURL = this.usersURL + 'users/setUserPreferences';
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		const httpOptions = { headers: httpHeaders };

		// Create the primary user information.
		const formData = { 'username': user.username,
							'preferences': user.preferences,
							'application': this.applicationName };

		console.log('Saving user preferences');
		return this.http.post(setUserPreferencesURL, formData, httpOptions );
	}

	//
	// queryForUsers -
	// call web services to get all users
	//
	queryForUsers(): Observable<any> {
		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })};

		const allUserJSON = this.http.get(this.usersURL + 'users/getWithRoles', httpOptions );
		return allUserJSON;
	}

	// Delete user using username.
	// Updated for new user web services.
	deleteUser(username): Observable<any> {
		const deleteUsersURL = this.usersURL + 'users/' + username;
		let httpHeaders = new HttpHeaders();
		httpHeaders = httpHeaders.append ( 'Content-Type', 'application/json' );
		httpHeaders = httpHeaders.append ( 'Authorization', this.authService.getAuthorization());
		console.log(this.authService.getAuthorization());

		const httpOptions = { headers: httpHeaders };

		return this.http.delete( deleteUsersURL, httpOptions );
	}
}
